import {
  mapCategoryToEdgeCategory,
  mapEdgeCategoryToCategory,
} from '@modules/common/utils';
import { REASON_NAME_ERRORS } from '@modules/taxonomy/helpers/getRreasonNameErrors';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { ArrowBackRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GeneratedRcInput,
  MapIdToStringInput,
  UnassignedRcInput,
} from 'generated/graphql';
import { useRef } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { toTitleCase } from 'v2/utils/strings';
import { shallow } from 'zustand/shallow';
import { useMuiModalContext } from '../Modal/ModalContext';
import { useNotificationContext } from '../Notification/NotificationContext';
import { CancelCreateNewReasonDialog } from './CancelCreateNewReasonDialog';
import { CreateReasonSuccessCallback } from './CreateNewReasonV3Modal';
import {
  getSelectedReasonDefinitions,
  getSelectedUnassignedDefinitions,
  trackCreateNewReasonEvents,
} from './helpers';
import {
  useCreateReason,
  useGetReasonDefinitions,
  useGetReasonNameForRCs,
} from './hooks';

interface CreateNewReasonV3ModalFooterProps {
  onCloseDialog: () => void;
  onCreateReason: (opts: CreateReasonSuccessCallback) => void;
}

export const CreateNewReasonV3ModalFooter = ({
  onCloseDialog,
  onCreateReason,
}: CreateNewReasonV3ModalFooterProps) => {
  const abortController = useRef(new AbortController());

  const { showErrorNotification } = useNotificationContext();

  const { openModal, closeModal: closeCancelDialog } = useMuiModalContext();

  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);
  const {
    reason,
    activeStep,
    description,
    isEditPromptMode,
    reasonDefinitions,
    selectedReasonDefinition,
  } = useTaxonomyStore(
    ({
      createReasonV3State: {
        reason,
        misc,
        visual,
        activeStep,
        description,
        reasonDefinitions,
      },
    }) => ({
      reason,
      activeStep,
      description,
      reasonDefinitions,
      isEditPromptMode: visual?.isEditPromptMode,
      selectedReasonDefinition: misc?.selectedReasonDefinition,
    }),
    shallow,
  );

  const [getReasonDefinitions, { loading: isFetchingReasonDefinitions }] =
    useGetReasonDefinitions({
      abortSignal: abortController.current.signal,
    });

  const [getReasonNameForRC, { isLoading: isGeneratingReasonNameLoading }] =
    useGetReasonNameForRCs({
      abortSignal: abortController.current.signal,
      onCompleted: ({ displayName, category }) => {
        dispatch({
          type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
          payload: {
            reason: {
              name: toTitleCase(displayName),
              category: mapEdgeCategoryToCategory(category),
            },
          },
        });
      },
    });

  const [createReason, { loading: isReasonCreating }] = useCreateReason({
    onError: (error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        dispatch({
          type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
          payload: {
            visual: {
              reasonNameError: REASON_NAME_ERRORS.NAME_ALREADY_EXISTS,
            },
          },
        });
        return;
      }
      showErrorNotification({
        contentComponent: `Failed to create Reason. Error: ${error.message}`,
        props: {},
      });
    },
    onCompleted: (reasonID) => {
      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          reason: {
            id: reasonID,
          },
        },
      });
    },
  });

  const selectedGeneratedRCs = reasonDefinitions
    ?.filter((definition) => definition.__typename === 'GeneratedRC')
    .map((definition): GeneratedRcInput => {
      return {
        name: definition.name,
        category: definition.category,
      };
    });

  const selectedUnassignedDefinitions = getSelectedUnassignedDefinitions(
    reasonDefinitions,
  ).map((definition): UnassignedRcInput => {
    return {
      id: definition.id,
      name: definition.name,
      category: definition.category,
      recordIDs: definition.recordIDs,
    };
  });

  const onCreateReasonClick = async () => {
    if (!reason?.name) {
      return;
    }

    const feedbackRecords = selectedUnassignedDefinitions.map(
      (definition): MapIdToStringInput => {
        return {
          rc: definition.name,
          recordID: definition.recordIDs[0] ?? '',
        };
      },
    );

    const { data } = await createReason({
      description,
      displayName: toTitleCase(reason.name),
      metadata: {
        category: mapCategoryToEdgeCategory(reason.category),
        rc: {
          generatedRCs: selectedGeneratedRCs ?? [],
          unassignedRCs: selectedUnassignedDefinitions ?? [],
        },
      },
      records: feedbackRecords,
    });

    const reasonID = data?.createReasonV2?.id;
    if (reasonID && reason.category) {
      onCreateReason?.({
        reasonID,
        reasonName: reason.name,
        reasonCategory: reason.category,
      });
    }

    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Reason Created',
      description,
      createdReason: {
        name: toTitleCase(reason.name),
        category: reason.category,
      },
    });
  };

  const moveToNextStep = async () => {
    if (!description) {
      return;
    }

    try {
      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Description Submitted',
        description,
        selectedRC: selectedReasonDefinition?.name,
      });
    } catch {}

    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        visual: {
          isFetchingReasonDefinitions: true,
        },
      },
    });

    const { error, definitions } = await getReasonDefinitions(description);

    // Don't move to next step if API returns an error
    // The error notification is triggered from the hook callback
    if (error) {
      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          visual: {
            isFetchingReasonDefinitions: false,
          },
        },
      });

      return;
    }

    await getReasonNameForRC(getSelectedReasonDefinitions(definitions));

    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        activeStep: 2,
        reasonDefinitions: definitions,
        visual: {
          isFetchingReasonDefinitions: false,
        },
      },
    });
  };

  const moveToPreviousStep = () => {
    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        activeStep: 1,
      },
    });
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Go Back CTA Clicked',
    });
  };

  const onCancelClick = () => {
    const abortAPICalls = () => {
      abortController.current.abort();
      // Reset the signal
      abortController.current = new AbortController();
    };

    const onClose = () => {
      abortAPICalls();
      onCloseDialog();
    };

    // check if there are unsaved changes
    if (description) {
      openModal({
        contentComponent: () => (
          <CancelCreateNewReasonDialog
            closeCancelDialog={closeCancelDialog}
            closeCreateNewReasonDialog={onClose}
          />
        ),
        modalProps: {
          shouldDisableBackdropClick: true,
        },
      });
      return;
    }

    onClose();
  };

  const renderPrimaryActionButton = () => {
    if (activeStep === 1) {
      return (
        <Tooltip
          arrow
          title={
            !description
              ? 'Please describe the customer feedback before proceeding'
              : ''
          }
        >
          <Box>
            <LoadingButton
              loading={
                isFetchingReasonDefinitions || isGeneratingReasonNameLoading
              }
              variant="contained"
              onClick={moveToNextStep}
              disabled={!description}
            >
              Next
            </LoadingButton>
          </Box>
        </Tooltip>
      );
    }

    const isEmptySelectedDefinitions =
      !selectedGeneratedRCs?.length && !selectedUnassignedDefinitions.length;

    const isEmptyReason = !reason?.name || !reason.category;

    return (
      <LoadingButton
        variant="contained"
        loading={isReasonCreating}
        onClick={onCreateReasonClick}
        disabled={
          isEmptySelectedDefinitions || isEmptyReason || isEditPromptMode
        }
      >
        Create Reason
      </LoadingButton>
    );
  };

  return (
    <DialogActions
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 1,
        mx: 2,
      }}
    >
      <Typography variant="subtitle2">Step {activeStep} of 2</Typography>
      <Stack direction="row" spacing={1}>
        <Stack direction="row" spacing={0.5}>
          <Button variant="text" onClick={onCancelClick}>
            Cancel
          </Button>
          <Divider
            flexItem
            variant="middle"
            orientation="vertical"
            sx={{ height: 'auto !important' }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          {activeStep !== 1 && (
            <Button
              variant="outlined"
              disabled={isEditPromptMode}
              onClick={moveToPreviousStep}
              startIcon={<ArrowBackRounded />}
            >
              Back
            </Button>
          )}
          {renderPrimaryActionButton()}
        </Stack>
      </Stack>
    </DialogActions>
  );
};
